import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Checkbox, Divider, FormControlLabel, Menu, Tooltip } from "@mui/material";
import { formatVatType } from "components/Beneficiar/utils";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccountingMappingGect } from "store/types";
import { BeneficiaryVAT, GECT_VAT_AMOUNT_TYPES } from "store/types";

/**
 * Max lengths of the Cegid GECT import, and width of each field on the line (the label takes the remaining space)
 */
const TEXT_FIELDS: Array<{ key: keyof AccountingMappingGect; maxLength: number; width: string }> = [
    { key: "documentType", maxLength: 4, width: "5.5rem" },
    { key: "label", maxLength: 100, width: "auto" },
    { key: "poste", maxLength: 20, width: "6rem" },
    { key: "budgetControl", maxLength: 4, width: "3.5rem" },
    { key: "analyticThirdParty", maxLength: 10, width: "6.5rem" },
    { key: "vatCode", maxLength: 10, width: "4.5rem" },
    { key: "articleNature", maxLength: 10, width: "4.5rem" },
];

const CHECKBOX_FIELDS = ["vatDeclarationOnly", "omitContractParams", "contractNumbers"] as const;

/**
 * French VAT profiles a line can be restricted to
 */
const VAT_PROFILES = [
    BeneficiaryVAT.RETENUE_SOURCE,
    BeneficiaryVAT.VAT,
    BeneficiaryVAT.NONE,
    BeneficiaryVAT.TWENTY_PERCENT,
] as const;

/**
 * Adds or removes a VAT profile, every profile when none is left
 */
export const toggleVatProfile = (vatProfiles: BeneficiaryVAT[] | undefined, profile: BeneficiaryVAT) => {
    const next = vatProfiles?.includes(profile)
        ? vatProfiles.filter((value) => value !== profile)
        : [...(vatProfiles ?? []), profile].sort();
    return next.length ? next : undefined;
};

const LABEL_VARIABLES = "{{dossier}}, {{name}}, {{firstname}}, {{title}}, {{year}}, {{periodStart}}, {{periodEnd}}";

interface IGectMappingFields {
    gect: AccountingMappingGect;
    onChange: (gect: AccountingMappingGect) => void;
}

/**
 * Small caption above a field, the full name is shown on hover
 */
const FieldCaption = ({ name, short, info }: { name: string; short: string; info?: string }) => (
    <Tooltip title={info ?? name} placement="top">
        <span className="gect-field-caption">
            {short}
            {info && <InfoOutlinedIcon className="gect-field-caption-icon" />}
        </span>
    </Tooltip>
);

/**
 * Digits only: the backend accepts a due number from 0 to 9999
 */
export const parseDueNumber = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    return digits === "" ? undefined : Number(digits);
};

const GectMappingFields = ({ gect, onChange }: IGectMappingFields) => {
    const [t] = useTranslation();
    const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);

    const change = <K extends keyof AccountingMappingGect>(key: K, value: AccountingMappingGect[K]) =>
        onChange({ ...gect, [key]: value });

    const checkedOptions = CHECKBOX_FIELDS.filter((key) => !!gect[key]).length + (gect.vatProfiles?.length ? 1 : 0);

    return (
        <div className="gect-mapping-fields">
            {TEXT_FIELDS.map(({ key, maxLength, width }) => (
                <Fragment key={key}>
                    <div className={`gect-field${key === "label" ? " grow" : ""}`} style={{ width }}>
                        <FieldCaption
                            name={t(`Accounting.Gect.${key}`)}
                            short={t(`Accounting.Gect.Short.${key}`)}
                            info={
                                key === "label" ? `${t("Accounting.Gect.LabelHelper")} ${LABEL_VARIABLES}` : undefined
                            }
                        />
                        <StyledTextField
                            size="small"
                            fullWidth
                            value={(gect[key] as string | undefined) ?? ""}
                            onChange={(e) => change(key, e.target.value || undefined)}
                            inputProps={{ maxLength, "aria-label": t(`Accounting.Gect.${key}`) }}
                        />
                    </div>
                    {key === "vatCode" && (
                        <div className="gect-field" style={{ width: "7rem" }}>
                            <FieldCaption
                                name={t("Accounting.Gect.vatAmount")}
                                short={t("Accounting.Gect.Short.vatAmount")}
                            />
                            <StyledSelect
                                size="small"
                                fullWidth
                                displayEmpty
                                value={gect.vatAmount ?? ""}
                                IconComponent={ExpandMoreIcon}
                                MenuProps={{ className: "select-menu" }}
                                inputProps={{ "aria-label": t("Accounting.Gect.vatAmount") }}
                                onChange={(e) =>
                                    change(
                                        "vatAmount",
                                        (e.target.value || undefined) as AccountingMappingGect["vatAmount"],
                                    )
                                }
                            >
                                <StyledMenuItem value="" className="select-menu-item">
                                    {t("Accounting.Gect.NoVatAmount")}
                                </StyledMenuItem>
                                {GECT_VAT_AMOUNT_TYPES.map((type) => (
                                    <StyledMenuItem key={type} value={type} className="select-menu-item">
                                        {t(`Accounting.Form.${type}`)}
                                    </StyledMenuItem>
                                ))}
                            </StyledSelect>
                        </div>
                    )}
                </Fragment>
            ))}
            <div className="gect-field" style={{ width: "3.5rem" }}>
                <FieldCaption name={t("Accounting.Gect.dueNumber")} short={t("Accounting.Gect.Short.dueNumber")} />
                <StyledTextField
                    size="small"
                    fullWidth
                    type="number"
                    value={gect.dueNumber ?? ""}
                    onChange={(e) => change("dueNumber", parseDueNumber(e.target.value))}
                    inputProps={{ min: 0, max: 9999, "aria-label": t("Accounting.Gect.dueNumber") }}
                />
            </div>
            <Button
                size="small"
                variant="outlined"
                className="gect-options-btn"
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => setOptionsAnchor(e.currentTarget)}
            >
                {t("Accounting.Gect.Options")}
                {checkedOptions > 0 && ` (${checkedOptions})`}
            </Button>
            <Menu anchorEl={optionsAnchor} open={!!optionsAnchor} onClose={() => setOptionsAnchor(null)}>
                {CHECKBOX_FIELDS.map((key) => (
                    <FormControlLabel
                        key={key}
                        className="gect-option"
                        sx={{ display: "flex", margin: 0, padding: "0 1rem 0 0.5rem" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={!!gect[key]}
                                onChange={(e) => change(key, e.target.checked)}
                            />
                        }
                        label={t(`Accounting.Gect.${key}`)}
                    />
                ))}
                <Divider />
                <Tooltip title={t("Accounting.Gect.vatProfilesHelper")} placement="left">
                    <span className="gect-options-title">{t("Accounting.Gect.vatProfiles")}</span>
                </Tooltip>
                {VAT_PROFILES.map((profile) => (
                    <FormControlLabel
                        key={profile}
                        className="gect-option"
                        sx={{ display: "flex", margin: 0, padding: "0 1rem 0 0.5rem" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={!!gect.vatProfiles?.includes(profile)}
                                onChange={() => change("vatProfiles", toggleVatProfile(gect.vatProfiles, profile))}
                            />
                        }
                        label={formatVatType(profile)}
                    />
                ))}
            </Menu>
        </div>
    );
};

export default GectMappingFields;
